import * as React from 'react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { Search, FileSignature, Calendar, FileText, DollarSign, Download, Loader2 } from 'lucide-react'
import { getOrders, generateDispatchDocument } from '@/api/orders'
import type { Order } from '@/api/orders'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

/** 乘客姓名中間遮蔽：2字「陳O」，3字以上頭尾保留、中間全部換成 O，例如「陳O志」 */
function maskName(name?: string): string {
  if (!name) return ''
  const chars = [...name]
  if (chars.length <= 1) return name
  if (chars.length === 2) return `${chars[0]}O`
  return `${chars[0]}${'O'.repeat(chars.length - 2)}${chars[chars.length - 1]}`
}

/** 電話去掉中間 4 碼：保留前 4 碼＋後 2 碼，中間換成 OOOO，例如 0912OOOO12 */
function maskPhone(phone?: string): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 8) return phone
  return `${digits.slice(0, 4)}OOOO${digits.slice(-2)}`
}

/** 從備註裡取出「公司：XXX」（V2 公用訂車表單會把公司名稱寫進備註） */
function extractCompanyName(notes?: string): string {
  if (!notes) return ''
  const m = notes.match(/公司[:：]\s*([^、]+)/)
  return m ? m[1].trim() : ''
}

export default function XunanSignReportPage() {
  const navigate = useNavigate()
  const [dateFrom, setDateFrom] = React.useState(() => {
    const d = new Date()
    d.setDate(1)
    return format(d, 'yyyy-MM-dd')
  })
  const [dateTo, setDateTo] = React.useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [orders, setOrders] = React.useState<Order[]>([])
  const [loading, setLoading] = React.useState(false)
  const [searched, setSearched] = React.useState(false)
  const [attachDispatch, setAttachDispatch] = React.useState(false)
  const [exportingPdf, setExportingPdf] = React.useState(false)

  React.useEffect(() => {
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = async () => {
    setLoading(true)
    try {
      const res = await getOrders({
        limit: 1000,
        date_from: dateFrom,
        date_to: dateTo,
      })
      // 派車單樣板 = 迅安簽單的訂單（見 OrderDrawer 的「迅安簽單」勾選）
      // 一律依日期時間排序，表格、Excel、PDF（含附加的派車單影像）都用同一份排好的資料
      const xunanOrders = res.data
        .filter(o => o.dispatch_template_key === 'xunan')
        .sort((a, b) => (a.pickup_datetime || '').localeCompare(b.pickup_datetime || ''))
      setOrders(xunanOrders)
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const totalAmount = Math.round(orders.reduce((sum, o) => sum + (o.pricing?.total_amount || 0), 0))
  const signedCount = orders.filter(o => !!o.signature_image_path).length

  const handleExportExcel = async () => {
    const { utils, writeFile } = await import('xlsx')
    const headers = ['#', '訂單編號', '公司名稱', '日期時間', '乘客姓名', '乘客電話', '上車地點', '下車地點', '司機', '車號', '金額', '已簽名']
    const rows = orders.map((order, idx) => [
      idx + 1,
      order.order_number,
      extractCompanyName(order.driver_notes),
      order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-',
      maskName(order.passenger_name),
      maskPhone(order.passenger_phone),
      order.pickup_location || '',
      order.dropoff_location || '',
      order.driver_name || '',
      order.vehicle_number || '',
      Math.round(order.pricing?.total_amount || 0),
      order.signature_image_path ? '是' : '否',
    ])
    const wsData = [
      [`迅安簽單報表`],
      [`期間：${dateFrom} ～ ${dateTo}`],
      [],
      headers,
      ...rows,
      [],
      ['', '', '', '', '', '', '', '', '', '合計', totalAmount, ''],
    ]
    const ws = utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 4 }, { wch: 20 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 8 }]
    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, '迅安簽單報表')
    writeFile(wb, `迅安簽單報表_${dateFrom}_${dateTo}.xlsx`)
  }

  // 將圖片 Blob 轉成 data URL，供 <img> 直接嵌入列印內容
  const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })

  const handleExportPDF = async () => {
    setExportingPdf(true)
    let attachmentsHtml = ''
    try {
      if (attachDispatch) {
        const images: string[] = []
        for (const order of orders) {
          try {
            const blob = await generateDispatchDocument(order.id, true)
            images.push(await blobToDataUrl(blob))
          } catch (err) {
            console.error(`派車單生成失敗 (${order.order_number}):`, err)
          }
        }
        // 2 張簽單排在同一張 A4（直向堆疊），每 2 張換一頁
        const pairedPages: string[][] = []
        for (let i = 0; i < images.length; i += 2) pairedPages.push(images.slice(i, i + 2))
        attachmentsHtml = pairedPages.map(pair => `
          <div class="attachment-page">
            ${pair.map(src => `<img src="${src}" />`).join('')}
          </div>
        `).join('')
      }

      const rows = orders.map((order, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${order.order_number}<br/><span class="sub">${extractCompanyName(order.driver_notes)}</span></td>
          <td>${order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-'}</td>
          <td>${maskName(order.passenger_name)}<br/><span class="sub">${maskPhone(order.passenger_phone)}</span></td>
          <td>${order.pickup_location || ''}</td>
          <td>${order.dropoff_location || ''}</td>
          <td>${order.driver_name || ''}${order.vehicle_number ? ' / ' + order.vehicle_number : ''}</td>
          <td style="text-align:right">$${Math.round(order.pricing?.total_amount || 0).toLocaleString()}</td>
        </tr>
      `).join('')

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>迅安簽單報表</title>
  <style>
    @page { size: A4 portrait; margin: 15mm; }
    body { font-family: "Microsoft JhengHei", "PingFang TC", sans-serif; font-size: 11px; color: #111; }
    h2 { font-size: 16px; margin: 0 0 4px; }
    p { margin: 0 0 12px; color: #555; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #7c3aed; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; }
    td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
    td .sub { color: #888; font-size: 9px; }
    tr:nth-child(even) td { background: #f9fafb; }
    .total-row td { font-weight: bold; background: #f5f3ff; border-top: 2px solid #7c3aed; }
    .attachment-page { page-break-before: always; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8mm; height: 100%; }
    .attachment-page img { max-width: 100%; max-height: 48%; object-fit: contain; }
  </style>
</head>
<body>
  <h2>迅安簽單報表</h2>
  <p>期間：${dateFrom} ～ ${dateTo}　共 ${orders.length} 筆</p>
  <table>
    <thead>
      <tr>
        <th>#</th><th>訂單編號／公司名稱</th><th>日期時間</th><th>乘客姓名／電話</th>
        <th>上車地點</th><th>下車地點</th><th>司機／車號</th><th>金額</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr class="total-row">
        <td colspan="6"></td>
        <td>合計</td><td style="text-align:right">$${totalAmount.toLocaleString()}</td>
      </tr>
    </tfoot>
  </table>
  ${attachmentsHtml}
</body>
</html>`

      const win = window.open('', '_blank')
      if (!win) return
      win.document.write(html)
      win.document.close()
      win.onload = () => { win.print() }
    } finally {
      setExportingPdf(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/30">
          <FileSignature className="h-5 w-5 text-purple-600 dark:text-purple-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">迅安簽單報表</h1>
      </div>

      {/* 篩選條件 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />快速選擇</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const now = new Date()
                  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                  const last = new Date(now.getFullYear(), now.getMonth(), 0)
                  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
                  setDateFrom(fmt(first))
                  setDateTo(fmt(last))
                }}
                className="h-10 px-4 rounded-md border border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-700 text-sm font-semibold text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors cursor-pointer"
              >
                上個月
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date()
                  const first = new Date(now.getFullYear(), now.getMonth(), 1)
                  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
                  setDateFrom(fmt(first))
                  setDateTo(fmt(last))
                }}
                className="h-10 px-4 rounded-md border border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-700 text-sm font-semibold text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors cursor-pointer"
              >
                本月
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />開始日期</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />結束日期</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading}
            className="h-10 px-6 bg-purple-600 hover:bg-purple-700 text-white font-bold"
          >
            <Search className="h-4 w-4 mr-2" />
            {loading ? '查詢中...' : '查詢'}
          </Button>
        </div>
      </div>

      {/* 統計卡片 */}
      {searched && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><FileText className="h-4 w-4" />迅安簽單訂單數</div>
            <div className="text-2xl font-black text-gray-900 dark:text-white">{orders.length} 筆</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><FileSignature className="h-4 w-4" />已完成簽名</div>
            <div className="text-2xl font-black text-purple-600 dark:text-purple-400">{signedCount} 筆</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><DollarSign className="h-4 w-4" />總金額</div>
            <div className="text-2xl font-black text-green-600 dark:text-green-400">${totalAmount.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* 訂單明細 */}
      {searched && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
            <span className="font-bold text-gray-900 dark:text-white text-sm">訂單明細</span>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-gray-500">{dateFrom} ～ {dateTo}</span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox checked={attachDispatch} onCheckedChange={v => setAttachDispatch(!!v)} />
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">PDF附上派車單（簽名）</span>
              </label>
              {orders.length > 0 && (
                <>
                  <Button
                    onClick={handleExportPDF}
                    variant="outline"
                    disabled={exportingPdf}
                    className="h-8 px-3 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    {exportingPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                    {exportingPdf ? '產生中...' : 'PDF'}
                  </Button>
                  <Button
                    onClick={handleExportExcel}
                    variant="outline"
                    className="h-8 px-3 text-xs border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Excel
                  </Button>
                </>
              )}
            </div>
          </div>
          {orders.length === 0 ? (
            <div className="py-16 text-center text-gray-400">此期間沒有勾選迅安簽單的訂單</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">訂單編號／公司名稱</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">日期</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">乘客姓名／電話</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">上下車地點</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">司機／車號</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">金額</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">簽名</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {orders.map((order, idx) => (
                    <tr
                      key={order.id}
                      className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-slate-50 dark:bg-gray-750'} hover:bg-purple-50/50 dark:hover:bg-purple-900/10 cursor-pointer`}
                      onClick={() => navigate(`/orders?editOrder=${order.id}`)}
                    >
                      <td className="px-3 py-3 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">
                        <div className="font-medium text-gray-900 dark:text-white">{order.order_number}</div>
                        <div className="text-gray-400">{extractCompanyName(order.driver_notes) || '-'}</div>
                      </td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                        {order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-'}
                      </td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">
                        <div className="text-gray-700 dark:text-gray-300">{maskName(order.passenger_name)}</div>
                        <div className="text-gray-400">{maskPhone(order.passenger_phone)}</div>
                      </td>
                      <td className="px-3 py-3 max-w-[220px]">
                        <div className="text-xs text-gray-700 dark:text-gray-300 truncate">{order.pickup_location}</div>
                        <div className="text-xs text-gray-400 truncate">→ {order.dropoff_location}</div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {order.driver_name || '-'}{order.vehicle_number ? ` / ${order.vehicle_number}` : ''}
                      </td>
                      <td className="px-3 py-3 text-right text-xs font-bold text-gray-900 dark:text-white">
                        ${Math.round(order.pricing?.total_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {order.signature_image_path ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">已簽名</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">未簽名</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-purple-50 dark:bg-purple-900/20 border-t-2 border-purple-200 dark:border-purple-700">
                  <tr>
                    <td colSpan={6} className="px-3 py-3 font-bold text-gray-700 dark:text-gray-300">合計 {orders.length} 筆</td>
                    <td className="px-3 py-3 text-right font-black text-gray-900 dark:text-white text-base">
                      ${totalAmount.toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
